/**
 * CreateScraperModal Component
 *
 * Modal for creating new scrapers
 */

import { useState } from "react";
import { FaServer, FaTimes, FaSpinner } from "react-icons/fa";
import { useScrapersData } from "../../hooks";
import "./CreateScraperModal.scss";

export const CreateScraperModal = ({ isOpen, onClose, onSuccess }) => {
  const { createScraper } = useScrapersData();
  const [formData, setFormData] = useState({
    name: "",
    url: "",
    scraperId: "",
    apiKey: "",
    platform: "local",
    availability: true,
    healthStatus: "unknown",
    notes: "",
  });
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (
      !formData.name.trim() ||
      !formData.url.trim() ||
      !formData.scraperId.trim()
    ) {
      return;
    }

    setLoading(true);
    try {
      const scraperId = await createScraper(formData);

      if (scraperId) {
        // Reset form
        setFormData({
          name: "",
          url: "",
          scraperId: "",
          apiKey: "",
          platform: "local",
          availability: true,
          healthStatus: "unknown",
          notes: "",
        });

        // Call success callback
        if (onSuccess) {
          onSuccess(scraperId);
        }

        // Close modal
        onClose();
      }
    } catch (error) {
      console.error("Error creating scraper:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      setFormData({
        name: "",
        url: "",
        scraperId: "",
        apiKey: "",
        platform: "local",
        availability: true,
        healthStatus: "unknown",
        notes: "",
      });
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="create-scraper-modal-overlay" onClick={handleClose}>
      <div
        className="create-scraper-modal"
        onClick={(e) => e.stopPropagation()}
      >
        <form onSubmit={handleSubmit}>
          <div className="create-scraper-modal__header">
            <div className="create-scraper-modal__title">
              <FaServer />
              Create New Scraper
            </div>
            <button
              type="button"
              className="create-scraper-modal__close"
              onClick={handleClose}
              disabled={loading}
            >
              <FaTimes />
            </button>
          </div>

          <div className="create-scraper-modal__body">
            <p className="subtitle">
              Add a new scraper service to manage property scraping operations.
              Each scraper can handle multiple scrapes and will be selected
              using least-recently-used routing.
            </p>

            <div className="form-group">
              <label htmlFor="name">
                Scraper Name <span className="required">*</span>
              </label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                placeholder="e.g., Railway Production Scraper"
                required
                disabled={loading}
              />
            </div>

            <div className="form-group">
              <label htmlFor="url">
                Scraper URL <span className="required">*</span>
              </label>
              <input
                type="url"
                id="url"
                name="url"
                value={formData.url}
                onChange={handleChange}
                placeholder="e.g., https://scraper.example.com"
                required
                disabled={loading}
              />
              <span className="help-text">
                The full URL where this scraper service is hosted
              </span>
            </div>

            <div className="form-group">
              <label htmlFor="scraperId">
                Scraper ID <span className="required">*</span>
              </label>
              <input
                type="text"
                id="scraperId"
                name="scraperId"
                value={formData.scraperId}
                onChange={handleChange}
                placeholder="e.g., railway-scraper-1"
                required
                disabled={loading}
              />
              <span className="help-text">
                Unique identifier matching the SCRAPER_ID environment variable
              </span>
            </div>

            <div className="form-group">
              <label htmlFor="apiKey">
                API Key <span className="required">*</span>
              </label>
              <input
                type="password"
                id="apiKey"
                name="apiKey"
                value={formData.apiKey}
                onChange={handleChange}
                placeholder="Enter scraper API key"
                required
                disabled={loading}
              />
              <span className="help-text">
                API key for remote scrape authentication (X-API-Key header)
              </span>
            </div>

            <div className="form-group">
              <label htmlFor="platform">
                Platform <span className="required">*</span>
              </label>
              <select
                id="platform"
                name="platform"
                value={formData.platform}
                onChange={handleChange}
                required
                disabled={loading}
              >
                <option value="local">Local Development</option>
                <option value="railway">Railway</option>
                <option value="vps">VPS</option>
              </select>
            </div>

            <div className="form-group form-group--checkbox">
              <label htmlFor="availability">
                <input
                  type="checkbox"
                  id="availability"
                  name="availability"
                  checked={formData.availability}
                  onChange={handleChange}
                  disabled={loading}
                />
                <span>Scraper is available for use</span>
              </label>
              <span className="help-text">
                When enabled, this scraper will be included in LRU routing
              </span>
            </div>

            <div className="form-group">
              <label htmlFor="notes">Notes (Optional)</label>
              <textarea
                id="notes"
                name="notes"
                value={formData.notes}
                onChange={handleChange}
                placeholder="e.g., Running with all 5 proxies configured"
                rows="3"
                disabled={loading}
                style={{
                  width: "100%",
                  padding: "0.625rem 0.875rem",
                  border: "1px solid #d1d5db",
                  borderRadius: "0.375rem",
                  fontSize: "0.9375rem",
                  color: "#111827",
                  fontFamily: "'Plus Jakarta Sans', sans-serif",
                  resize: "vertical",
                }}
              />
              <span className="help-text">
                Additional information about this scraper
              </span>
            </div>
          </div>

          <div className="create-scraper-modal__footer">
            <button
              type="button"
              className="btn btn--secondary"
              onClick={handleClose}
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn btn--primary"
              disabled={
                loading ||
                !formData.name.trim() ||
                !formData.url.trim() ||
                !formData.scraperId.trim()
              }
            >
              {loading ? (
                <>
                  <FaSpinner className="spinner" />
                  Creating...
                </>
              ) : (
                "Create Scraper"
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
